import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiPauseCircle,
  FiPlayCircle,
  FiRefreshCw,
  FiMessageCircle,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import {
  fetchFacebookCommentConfigs,
  fetchCommentQuota,
  pauseFacebookCommentConfig,
  resumeFacebookCommentConfig,
  deleteFacebookCommentConfig,
  getErrorMessage,
} from "./services/facebookCommentService";

function formatDate(timestamp) {
  if (!timestamp) {
    return "—";
  }

  return new Date(timestamp).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const FacebookCommentConfigListScreen = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processingId, setProcessingId] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");
  const [quotaInfo, setQuotaInfo] = useState(null);

  const userId = localStorage.getItem("userId");
  const isCommentFeatureLocked = Boolean(quotaInfo && !quotaInfo.hasAccess);

  const loadConfigs = async ({ silent = false } = {}) => {
    if (!userId) {
      toast.error("Không tìm thấy userId hiện tại");
      setLoading(false);
      return;
    }

    if (!silent) {
      setRefreshing(true);
    }

    try {
      const [data, nextQuotaInfo] = await Promise.all([
        fetchFacebookCommentConfigs(userId),
        Promise.resolve(null), // fetchCommentQuota(userId).catch(() => null),
      ]);
      setConfigs(data);
      setQuotaInfo(nextQuotaInfo);
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể tải danh sách cấu hình"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadConfigs({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePauseResume = async (config) => {
    if (isCommentFeatureLocked && !config.enabled) {
      toast.error("Gói hiện tại chưa được sử dụng Auto Comment. Vui lòng nâng cấp lên gói Chuyên nghiệp trở lên.");
      return;
    }

    setProcessingId(config.id);
    try {
      if (config.enabled) {
        await pauseFacebookCommentConfig(userId, config.id);
        toast.success(`Đã tạm dừng "${config.name}"`);
      } else {
        await resumeFacebookCommentConfig(userId, config.id);
        toast.success(`Đã kích hoạt "${config.name}"`);
      }
      await loadConfigs({ silent: true });
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể cập nhật trạng thái"));
    } finally {
      setProcessingId("");
    }
  };

  const handleDelete = async (configId) => {
    setProcessingId(configId);
    try {
      await deleteFacebookCommentConfig(userId, configId);
      toast.success("Đã xóa cấu hình");
      setConfirmDeleteId("");
      await loadConfigs({ silent: true });
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể xóa cấu hình"));
    } finally {
      setProcessingId("");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-gray-600">
          Đang tải danh sách cấu hình...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium mb-3">
              <FiMessageCircle />
              Lead Conversion Engine
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Quản lý cấu hình ra đơn từ comment
            </h1>
            <p className="text-gray-600 mt-2 max-w-2xl">
              Mỗi cấu hình là một bộ rule, AI và cảnh báo lead cho nhóm
              fanpage bán hàng của bạn. Bạn có thể bật hoặc tắt từng cấu hình
              độc lập.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => loadConfigs()}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
              Làm mới
            </button>
            <button
              onClick={() =>
                navigate("/facebook-comment-automation?mode=new")
              }
              disabled={isCommentFeatureLocked}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FiPlus />
              Tạo cấu hình mới
            </button>
          </div>
        </div>
      </div>

      {isCommentFeatureLocked && (
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4">
          <div className="flex items-center gap-3">
            <FiAlertCircle className="text-amber-600 text-lg" />
            <div>
              <h2 className="text-sm font-semibold text-amber-900">
                Lead Conversion Engine chỉ khả dụng từ gói Chuyên nghiệp
              </h2>
              <p className="text-sm text-amber-700 mt-1">
                Bạn vẫn có thể xem cấu hình cũ, nhưng không thể tạo mới hoặc kích hoạt lại khi đang ở gói hiện tại.
              </p>
            </div>
          </div>
        </div>
      )}

      {configs.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiMessageCircle className="text-2xl text-blue-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Chưa có cấu hình nào
          </h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Tạo cấu hình đầu tiên để bắt đầu phát hiện lead và kích hoạt sale
            action từ comment trên các fanpage của bạn.
            </p>
          <button
            onClick={() =>
              navigate("/facebook-comment-automation?mode=new")
            }
            disabled={isCommentFeatureLocked}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FiPlus />
            Tạo cấu hình đầu tiên
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {configs.map((config) => {
            const pageCount = (config.selectedPages || []).length;
            const isProcessing = processingId === config.id;
            const isConfirmingDelete = confirmDeleteId === config.id;

            return (
              <div
                key={config.id}
                className={`group flex flex-col bg-white rounded-3xl border p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  config.enabled
                    ? "border-blue-200 shadow-[0_4px_20px_-4px_rgba(59,130,246,0.1)] ring-1 ring-blue-50/50"
                    : "border-gray-200 shadow-sm opacity-80 hover:opacity-100"
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-5">
                  <div className="min-w-0 pr-4">
                    <h3 className="text-xl font-bold text-gray-900 truncate mb-1.5 group-hover:text-blue-600 transition-colors">
                      {config.name || "Chưa đặt tên"}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-gray-500 font-medium">
                      <span className="flex items-center gap-1.5" title={config.id}>
                        <FiMessageCircle className="text-gray-400" />
                        {config.id.substring(0, 8)}...
                      </span>
                      <span className="flex items-center gap-1.5">
                        <FiRefreshCw className="text-gray-400" />
                        Cập nhật: {formatDate(config.updatedAt)}
                      </span>
                    </div>
                  </div>

                  <span
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                      config.enabled
                        ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200/50"
                        : "bg-gray-50 text-gray-500 ring-1 ring-gray-200/50"
                    }`}
                  >
                    {config.enabled ? (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    ) : (
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    )}
                    {config.enabled ? "Active" : "Paused"}
                  </span>
                </div>

                {/* Business Context (Prompt/Persona) */}
                {config.businessContext && (
                  <div className="mb-5 bg-slate-50/80 rounded-2xl p-4 border border-slate-100/60">
                    <p className="text-[11px] font-bold text-slate-400 mb-1.5 uppercase tracking-widest">
                      Ngữ cảnh & Vai trò
                    </p>
                    <p className="text-sm text-slate-700 line-clamp-2 leading-relaxed">
                      {config.businessContext}
                    </p>
                  </div>
                )}

                {/* Fanpages */}
                <div className="mt-auto mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                      Áp dụng cho {pageCount} Fanpage{pageCount !== 1 && "s"}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pageCount === 0 ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-100">
                        <FiAlertCircle /> Chưa cấu hình Fanpage
                      </span>
                    ) : (
                      <>
                        {(config.selectedPages || [])
                          .slice(0, 4)
                          .map((page) => (
                            <span
                              key={page.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50/60 text-blue-700 text-sm font-medium border border-blue-100/50 hover:bg-blue-100/50 transition-colors cursor-default"
                            >
                              {page.name || page.id}
                            </span>
                          ))}
                        {pageCount > 4 && (
                          <span className="inline-flex items-center justify-center px-3 py-1.5 rounded-xl bg-gray-50 text-gray-600 text-sm font-medium border border-gray-200/60">
                            +{pageCount - 4} trang khác
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-5 border-t border-gray-100 flex items-center justify-between gap-3 mt-auto">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() =>
                        navigate(`/facebook-comment-automation?configId=${config.id}`)
                      }
                      className="inline-flex items-center justify-center gap-2.5 px-6 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-600/20 transition-all active:scale-95"
                    >
                      <FiEdit2 className="text-[15px]" />
                      Cài đặt
                    </button>

                    <button
                      onClick={() => handlePauseResume(config)}
                      disabled={isProcessing || (isCommentFeatureLocked && !config.enabled)}
                      title={config.enabled ? "Tạm dừng auto comment" : "Kích hoạt auto comment"}
                      className={`inline-flex items-center justify-center w-11 h-11 rounded-xl transition-all active:scale-95 disabled:opacity-60 disabled:active:scale-100 text-lg ${
                        config.enabled
                          ? "bg-amber-50 text-amber-600 hover:bg-amber-100 hover:shadow-sm"
                          : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:shadow-sm"
                      }`}
                    >
                      {config.enabled ? <FiPauseCircle /> : <FiPlayCircle />}
                    </button>
                  </div>

                  {isConfirmingDelete ? (
                    <div className="flex items-center gap-2 p-1.5 rounded-xl bg-rose-50 border border-rose-100">
                      <button
                        onClick={() => handleDelete(config.id)}
                        disabled={isProcessing}
                        className="px-4 py-2 rounded-lg bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 transition shadow-sm active:scale-95"
                      >
                        Xóa luôn
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId("")}
                        className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 text-xs font-bold hover:bg-gray-50 transition active:scale-95"
                      >
                        Hủy
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(config.id)}
                      disabled={isProcessing}
                      title="Xóa cấu hình này"
                      className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gray-50 text-gray-400 hover:bg-rose-50 hover:text-rose-600 transition-all active:scale-95 disabled:opacity-60"
                    >
                      <FiTrash2 className="text-[17px]" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FacebookCommentConfigListScreen;
